import React, { useState } from 'react';
import SafeIcon from '../../../common/SafeIcon';
import { formatCurrency, formatDate, calculateDaysUntil } from '../../../utils/helpers';
import BookMeetingModal from '../../modals/BookMeetingModal';
import SendContractModal from '../../modals/SendContractModal';

export default function ProjectOverview({ project, onEdit }) {
  const { description, client_contacts, organization, target_segment } = project;
  
  const [isMeetingModalOpen, setIsMeetingModalOpen] = useState(false);
  const [isContractModalOpen, setIsContractModalOpen] = useState(false);

  // Calculate stats
  const daysLeft = project.end_date ? calculateDaysUntil(project.end_date) : 0;

  // Grupper kontakter basert på rolle for Interessent-matrisen
  const getStakeholderGroup = (role) => {
    const r = (role || '').toLowerCase();
    if (r.includes('beslutter') || r.includes('ceo') || r.includes('leder')) return 'decision';
    if (r.includes('champion') || r.includes('ambassadør')) return 'champion';
    if (r.includes('blokker') || r.includes('skeptisk')) return 'blocker';
    return 'influencer';
  };

  const groupedContacts = {
    decision: client_contacts?.filter(c => getStakeholderGroup(c.role) === 'decision') || [],
    champion: client_contacts?.filter(c => getStakeholderGroup(c.role) === 'champion') || [],
    blocker: client_contacts?.filter(c => getStakeholderGroup(c.role) === 'blocker') || [],
    influencer: client_contacts?.filter(c => getStakeholderGroup(c.role) === 'influencer') || [],
  };

  const ContactCard = ({ contact, type }) => {
    const colors = {
      decision: 'bg-indigo-50 border-indigo-100 text-indigo-700',
      champion: 'bg-emerald-50 border-emerald-100 text-emerald-700',
      blocker: 'bg-rose-50 border-rose-100 text-rose-700',
      influencer: 'bg-slate-50 border-slate-100 text-slate-600'
    };
    const icons = {
      decision: '👑',
      champion: '⭐',
      blocker: '🛑',
      influencer: '👤'
    };

    return (
      <div className={`p-3 rounded-xl border ${colors[type]} flex items-center justify-between mb-2`}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-sm text-sm">
            <SafeIcon icon={icons[type]} />
          </div>
          <div>
            <p className="text-sm font-bold leading-none">{contact.name}</p>
            <p className="text-[10px] opacity-80 mt-1 uppercase tracking-wider">{contact.role || 'Ukjent rolle'}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {contact.email && <a href={`mailto:${contact.email}`} className="p-1.5 bg-white rounded-lg hover:bg-gray-50 transition-colors"><SafeIcon icon="📧" className="w-3 h-3" /></a>}
          {contact.phone && <a href={`tel:${contact.phone}`} className="p-1.5 bg-white rounded-lg hover:bg-gray-50 transition-colors"><SafeIcon icon="📞" className="w-3 h-3" /></a>}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {/* Top Section: Key Metrics & Timeline */}
      <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-slate-50 rounded-bl-full -mr-16 -mt-16 z-0"></div>
        <div className="relative z-10">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Timeline */}
            <div className="flex-1">
              <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest mb-4 flex items-center gap-2">
                <SafeIcon icon="📅" className="text-primary-600" /> Prosjektperiode
              </h3>
              <div className="flex items-center gap-4 mb-2">
                <div className="text-center">
                  <p className="text-[10px] text-slate-400 font-bold uppercase">Start</p>
                  <p className="text-sm font-bold text-slate-900">{project.start_date ? formatDate(project.start_date) : 'Ikke satt'}</p>
                </div>
                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden relative">
                  <div className="absolute inset-0 bg-slate-200 w-full"></div>
                </div>
                <div className="text-center">
                  <p className="text-[10px] text-slate-400 font-bold uppercase">Slutt</p>
                  <p className="text-sm font-bold text-slate-900">{project.end_date ? formatDate(project.end_date) : 'Ikke satt'}</p>
                </div>
              </div>
              {daysLeft > 0 && <p className="text-xs text-emerald-600 font-bold text-center bg-emerald-50 inline-block px-3 py-1 rounded-full">{daysLeft} dager igjen til frist</p>}
              {daysLeft < 0 && project.end_date && <p className="text-xs text-rose-600 font-bold text-center bg-rose-50 inline-block px-3 py-1 rounded-full">Frist utløpt</p>}
            </div>

            {/* Financials */}
            <div className="flex-1 border-l border-slate-100 pl-8">
              <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest mb-4 flex items-center gap-2">
                <SafeIcon icon="💰" className="text-primary-600" /> Økonomi & Mål
              </h3>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-[10px] text-slate-400 font-bold uppercase">Salgsmål</p>
                  <p className="text-2xl font-black text-emerald-600">{formatCurrency(project.target || 0, true)}</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 font-bold uppercase">Budsjett</p>
                  <p className="text-2xl font-black text-slate-700">{formatCurrency(project.budget || 0, true)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* COLUMN 1: Prosjektinfo */}
        <div className="lg:col-span-2 space-y-8">
          {/* Description Card */}
          <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm group relative">
            <button onClick={onEdit} className="absolute top-6 right-6 p-2 text-slate-300 hover:text-primary-600 hover:bg-slate-50 rounded-xl transition-all opacity-0 group-hover:opacity-100">
                <SafeIcon icon="✏️" />
            </button>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-blue-50 rounded-2xl text-blue-600">
                <SafeIcon icon="📋" className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-black text-slate-900">Om Prosjektet</h3>
            </div>
            <div className="prose prose-slate max-w-none">
              <p className="text-slate-600 leading-relaxed text-lg">
                {description || "Ingen beskrivelse lagt til. Klikk rediger for å definere omfanget."}
              </p>
            </div>
            <div className="mt-8 pt-8 border-t border-slate-100 grid grid-cols-2 gap-6">
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Målgruppe / Segment</p>
                <p className="text-sm font-bold text-slate-900">{target_segment || 'Alle segmenter'}</p>
              </div>
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Ansvarlig Avdeling</p>
                <p className="text-sm font-bold text-slate-900">{organization?.department || 'Generelt Salg'}</p>
              </div>
            </div>
          </div>

          {/* Stakeholder Matrix (Interessent-matrise) */}
          <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-purple-50 rounded-2xl text-purple-600">
                  <SafeIcon icon="🕸️" className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-xl font-black text-slate-900">Interessent-matrise</h3>
                  <p className="text-sm text-slate-500 font-medium">Hvem påvirker beslutningen?</p>
                </div>
              </div>
              <button onClick={onEdit} className="text-xs font-bold text-primary-600 hover:underline">+ Legg til person</button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <p className="text-xs font-black text-indigo-900 uppercase tracking-widest mb-2">👑 Beslutningstakere</p>
                {groupedContacts.decision.length > 0 ? groupedContacts.decision.map((c, i) => <ContactCard key={i} contact={c} type="decision" />) : <div className="p-4 border-2 border-dashed border-indigo-100 rounded-xl text-center text-xs text-indigo-400">Ingen definert</div>}
              </div>
              <div className="space-y-2">
                <p className="text-xs font-black text-emerald-900 uppercase tracking-widest mb-2">⭐ Ambassadører (Champions)</p>
                {groupedContacts.champion.length > 0 ? groupedContacts.champion.map((c, i) => <ContactCard key={i} contact={c} type="champion" />) : <div className="p-4 border-2 border-dashed border-emerald-100 rounded-xl text-center text-xs text-emerald-400">Ingen definert</div>}
              </div>
              <div className="space-y-2">
                <p className="text-xs font-black text-slate-600 uppercase tracking-widest mb-2">👤 Påvirkere / Brukere</p>
                {groupedContacts.influencer.length > 0 ? groupedContacts.influencer.map((c, i) => <ContactCard key={i} contact={c} type="influencer" />) : <div className="p-4 border-2 border-dashed border-slate-100 rounded-xl text-center text-xs text-slate-400">Ingen definert</div>}
              </div>
              <div className="space-y-2">
                <p className="text-xs font-black text-rose-900 uppercase tracking-widest mb-2">🛑 Skeptikere / Blokkere</p>
                {groupedContacts.blocker.length > 0 ? groupedContacts.blocker.map((c, i) => <ContactCard key={i} contact={c} type="blocker" />) : <div className="p-4 border-2 border-dashed border-rose-100 rounded-xl text-center text-xs text-rose-400">Ingen definert</div>}
              </div>
            </div>
          </div>
        </div>

        {/* COLUMN 2: Internt Team & Status */}
        <div className="lg:col-span-1 space-y-8">
          {/* Internal Team */}
          <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm">
            <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest mb-6">Internt Team</h3>
            <div className="space-y-4">
              <div>
                <p className="text-[10px] font-bold text-slate-400 mb-2">SALGSSJEF</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center font-bold text-xs">
                    {organization?.sales_manager?.substring(0, 2).toUpperCase() || 'SJ'}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-900">{organization?.sales_manager || 'Ikke tildelt'}</p>
                    <p className="text-xs text-slate-500">Overordnet ansvarlig</p>
                  </div>
                </div>
              </div>
              
              <div className="pt-4 border-t border-slate-100">
                <p className="text-[10px] font-bold text-slate-400 mb-2">SELGERE ({organization?.teams?.[0]?.sellers?.length || 0})</p>
                <div className="flex flex-wrap gap-2">
                  {organization?.teams?.[0]?.sellers?.map((seller, idx) => (
                    <div key={idx} className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-xl border border-slate-100">
                      <div className="w-6 h-6 rounded-full bg-white flex items-center justify-center text-[10px] font-bold border border-slate-200">
                        {seller.substring(0, 1)}
                      </div>
                      <span className="text-xs font-medium text-slate-700">{seller}</span>
                    </div>
                  ))}
                  <button onClick={onEdit} className="w-8 h-8 rounded-full border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400 hover:border-primary-500 hover:text-primary-500 transition-colors">
                    <SafeIcon icon="➕" className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 p-6 rounded-[2rem] text-white shadow-xl">
            <h3 className="text-sm font-black uppercase tracking-widest mb-4">Handlinger</h3>
            <div className="space-y-3">
              <button onClick={() => setIsMeetingModalOpen(true)} className="w-full py-3 bg-white/10 hover:bg-white/20 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all">
                <SafeIcon icon="📅" className="w-4 h-4" /> Book Strategimøte
              </button>
              <button onClick={() => setIsContractModalOpen(true)} className="w-full py-3 bg-primary-600 hover:bg-primary-500 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all shadow-lg">
                <SafeIcon icon="📄" className="w-4 h-4" /> Opprett Tilbud
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Action Modals */}
      <BookMeetingModal 
        isOpen={isMeetingModalOpen} 
        onClose={() => setIsMeetingModalOpen(false)} 
        initialData={{ title: `Møte: ${project.name}` }}
      />
      <SendContractModal
        isOpen={isContractModalOpen}
        onClose={() => setIsContractModalOpen(false)}
      />
    </div>
  );
}